import { useLocation, matchPath } from 'react-router-dom';
import { useQuery, type QueryFunction, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import {
  accountAssignmentKeys,
  fetchAccounts,
  fetchAssignedAccounts,
  fetchAccountsWithAssignments,
  fetchAccountsWithQuestionnaires,
  type AssignableContentType,
  type ClientAccount,
} from '../../api/accountAssignmentApi';

/**
 * The picker's list-route scopes. Wider than AssignableContentType on purpose:
 * that union doubles as the wire value for /assignments/*, so it must stay
 * 'lesson' | 'course'. Questionnaires are assigned in hoot, not through those
 * endpoints, and only ever scope the account list.
 */
export type ListScope = AssignableContentType | 'questionnaire';

export const DETAIL_BANNER_MESSAGES: Record<AssignableContentType, string> = {
  lesson: 'Editing client customizations on this lesson. Changes apply to this account only.',
  course: 'Editing client customizations on this course. Changes apply to this account only.',
};

export const LIST_BANNER_MESSAGES: Record<AssignableContentType, string> = {
  lesson: 'Showing lessons assigned to this account.',
  course: 'Showing courses assigned to this account.',
};

// Account-config-scoped pages (not per-item "assignable content" like lessons/courses,
// so they don't fit AssignableContentType) — matched by exact pathname.
export const STATIC_BANNER_MESSAGES: Record<string, string> = {
  '/image-library': "Showing images saved to this account's library.",
  '/centerstage': "Showing Emtrain's public video library. It is the same in every context.",
  '/theme-settings': 'Editing theme settings for this account.',
};

interface DetailRouteDescriptor {
  kind: AssignableContentType;
  id: string;
}

const DETAIL_ROUTE_PATTERNS: Array<{ path: string; kind: AssignableContentType }> = [
  { path: '/lessons/:id', kind: 'lesson' },
  { path: '/courses/:id', kind: 'course' },
];

const LIST_ROUTE_PATTERNS: Array<{ path: string; kind: ListScope }> = [
  { path: '/lessons', kind: 'lesson' },
  { path: '/courses', kind: 'course' },
  { path: '/assigned-questionnaires', kind: 'questionnaire' },
];

function matchDetailRoute(pathname: string): DetailRouteDescriptor | null {
  for (const { path, kind } of DETAIL_ROUTE_PATTERNS) {
    const match = matchPath({ path, end: true }, pathname);
    if (match) {
      const id = match.params['id'];
      if (id && id !== 'new') {
        return { kind, id };
      }
    }
  }
  return null;
}

function matchListRoute(pathname: string): ListScope | null {
  for (const { path, kind } of LIST_ROUTE_PATTERNS) {
    if (matchPath({ path, end: true }, pathname)) {
      return kind;
    }
  }
  return null;
}

/**
 * Route-derived scope for the client context picker — pure route → descriptor + banner copy,
 * no query subscription. Does NOT return the selected client context itself; see
 * useClientContext for that. Consumed by the banner (and by useClientContextAccounts).
 */
export function useClientContextRouteScope(): {
  descriptor: DetailRouteDescriptor | null;
  listKind: ListScope | null;
  bannerMessage: string | null;
} {
  const { pathname } = useLocation();
  const descriptor = matchDetailRoute(pathname);
  if (descriptor) {
    return { descriptor, listKind: null, bannerMessage: DETAIL_BANNER_MESSAGES[descriptor.kind] };
  }
  const listKind = matchListRoute(pathname);
  if (listKind) {
    // No banner for questionnaires: that screen carries its own subtitle, and adding
    // one would be a visible change beyond scoping the account list.
    const bannerMessage = listKind === 'questionnaire' ? null : LIST_BANNER_MESSAGES[listKind];
    return { descriptor: null, listKind, bannerMessage };
  }
  return { descriptor: null, listKind: null, bannerMessage: STATIC_BANNER_MESSAGES[pathname] ?? null };
}

/**
 * Pick the query key + fetcher for the picker's account list from the current route
 * scope. Keeping both in one branch guarantees the key and fetcher never drift apart.
 */
function resolveAccountsQuery(
  descriptor: DetailRouteDescriptor | null,
  listKind: ListScope | null,
): { queryKey: QueryKey; queryFn: QueryFunction<ClientAccount[]> } {
  if (descriptor) {
    return {
      queryKey: accountAssignmentKeys.assignedAccounts(descriptor.kind, descriptor.id),
      queryFn: () => fetchAssignedAccounts(descriptor.kind, descriptor.id),
    };
  }
  if (listKind === 'questionnaire') {
    return {
      queryKey: accountAssignmentKeys.accountsWithQuestionnaires(),
      queryFn: fetchAccountsWithQuestionnaires,
    };
  }
  if (listKind) {
    return {
      queryKey: accountAssignmentKeys.accountsWithAssignments(listKind),
      queryFn: () => fetchAccountsWithAssignments(listKind),
    };
  }
  return {
    queryKey: accountAssignmentKeys.accounts(),
    queryFn: fetchAccounts,
  };
}

/**
 * Route-switched account list for the client context picker — not the selected
 * context itself (see useClientContext). Consumed by the picker only.
 * - On detail routes: assigned accounts for the open content item.
 * - On list routes (/lessons, /courses): accounts that have ≥1 assignment for
 *   that list's content type (server aggregate).
 * - On /assigned-questionnaires: accounts that have ≥1 Author-Platform
 *   questionnaire (hoot aggregate, proxied through author-api).
 * - On other routes: full account list (same key as useClientAccounts → shared cache).
 *
 * Overrides the global placeholderData: previousData default to prevent
 * prior cached data from bleeding in when the query key changes.
 */
export function useClientContextAccounts(): {
  query: UseQueryResult<ClientAccount[]>;
  isScoped: boolean;
} {
  const { descriptor, listKind } = useClientContextRouteScope();

  const { queryKey, queryFn } = resolveAccountsQuery(descriptor, listKind);

  const query = useQuery<ClientAccount[]>({
    queryKey,
    queryFn,
    // Override the global placeholderData: previousData default (queryClient.ts line 35).
    // Without this, switching keys (list→detail, detail A→detail B) renders the
    // previous key's accounts as placeholder data — i.e. unassigned accounts on a
    // detail page, and a reset effect fed by wrong data.
    placeholderData: undefined,
  });

  return { query, isScoped: descriptor !== null || listKind !== null };
}
